import React, { useCallback, useEffect, useState } from "react";
import Icons from "../icons";
import Loader from "./Loader";
import AppPageArtifactSteps from "./AppPageArtifactSteps";
import AppPageVariables from "./AppPageVariables";
import AppPageActivityButton from "./AppPageActivityLog";
import AppPageDeleteButton from "./AppPageDelete";
import ExecutionResultView from "./ExecutionResultView";
import { IconButton, LiveRunModal, Modal, TONES, executionName, initials, plural } from "./TestRunsPanel";
import { stepToScript } from "../stepEnglish";
import ListSearch, { NoMatches, matchesQuery } from "./ListSearch";
import { useToast } from "./Toast";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

/* App Page › Executions — the Artifacts screen for App Pages:
     App Pages → Artifacts (Tests with a saved script) → Executions.
   Create records an execution of an Artifact; ▶ Execute replays its script
   once in a real browser (no AI). Everything is stored in Cortex
   (app_page_executions). */

const toneOf = (ex) => (ex.status === "ready" ? TONES.new : TONES[ex.status] || TONES.other);

function chipOf(ex) {
  if (ex.ai_fix?.status === "fixing") {
    return { label: "Regenerating script", cls: "bg-violet-50 text-violet-700 ring-violet-600/20", dot: "bg-violet-500 animate-pulse" };
  }
  const t = toneOf(ex);
  let label = t.label;
  if (ex.status === "running" && ex.live?.total) label = `Running ${ex.live.step}/${ex.live.total}`;
  if (ex.status === "blocked") label = "Blocked · page did not open";
  return { label, cls: t.pill, dot: t.dot };
}

function Chip({ chip, title }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-px text-[0.625rem] font-semibold ring-1 ring-inset ${chip.cls}`} title={title}>
      <span className={`h-1.5 w-1.5 rounded-full ${chip.dot}`} />
      {chip.label}
    </span>
  );
}

/* Stage 3: when a step fails, the script is regenerated by itself (no user
   action). The execution's `ai_fix` says where it is: pending (a step failed,
   the run still going), fixing, then fixed / failed / skipped (why not). The
   words are the user's, written here, so older records read the same. */
const AI_FIX = {
  pending: { title: "Step failed", box: "border-violet-200 bg-violet-50 text-violet-800", Icon: Icons.Spark, pulse: true },
  fixing: { title: "Regenerating the script", box: "border-violet-300 bg-violet-50 text-violet-800", Icon: Icons.Spinner, spin: true },
  fixed: { title: "Script regenerated", box: "border-emerald-200 bg-emerald-50 text-emerald-800", Icon: Icons.Check },
  failed: { title: "Could not regenerate the script", box: "border-rose-200 bg-rose-50 text-rose-700", Icon: Icons.Alert },
  skipped: { title: "Script not regenerated", box: "border-slate-200 bg-slate-50 text-slate-600", Icon: Icons.Alert },
};

/* The reason a repair failed or was skipped, without the stored message's lead-in (old or new wording). */
const REASON_LEAD =
  /^(The AI could not repair it|The AI repair stopped|The AI repair could not start|No AI repair|The script could not be regenerated|The script was not regenerated):\s*/i;

/* What is happening, in plain words: shown after the title, and in the live log. */
export function aiFixText(fix) {
  const step = fix?.step ? `Step ${fix.step}` : "A step";
  if (fix?.status === "pending") return `${step} failed. The script will be regenerated automatically when this run ends.`;
  if (fix?.status === "fixing") return `${step} failed, so the script is being regenerated.`;
  if (fix?.status === "fixed") return "Run it again to check the fix.";
  const reason = String(fix?.message || "").replace(REASON_LEAD, "").trim();
  return reason ? reason.charAt(0).toUpperCase() + reason.slice(1) : "";
}

/* Still working: keep polling. */
export const aiFixActive = (fix) => fix?.status === "pending" || fix?.status === "fixing";

export function AiFixNotice({ fix, compact = false }) {
  const look = fix && AI_FIX[fix.status];
  if (!look) return null;
  const { Icon } = look;
  return (
    <div
      role="status"
      aria-live="polite"
      className={`flex items-start gap-2 rounded-lg border ${compact ? "px-2.5 py-1.5 text-[0.6875rem]" : "px-4 py-3 text-sm"} ${look.box}`}
    >
      <Icon className={`mt-0.5 h-3.5 w-3.5 flex-none ${look.spin ? "animate-spin" : ""} ${look.pulse ? "animate-pulse" : ""}`} aria-hidden="true" />
      <span className="min-w-0 flex-1">
        <span className="font-semibold">{look.title}</span>
        {aiFixText(fix) ? <> · {aiFixText(fix)}</> : null}
        {!compact && fix.line && (
          <span className="mt-0.5 block truncate font-mono text-[0.6875rem] opacity-75" title={fix.line}>
            {fix.line}
          </span>
        )}
      </span>
    </div>
  );
}

/* Why the runner skipped an execution, in plain words (its stored reason when it is another one). */
function skipText(message) {
  const m = String(message || "");
  if (/@saves|may not save|would save/i.test(m)) return "this test saves, and this execution did not allow saving. Create a new execution to run it.";
  if (/placeholder/i.test(m))
    return "a step still had a placeholder (<…>), a value the AI found none of. Give it a value (Create asks for it), then create a new execution.";
  return m || "the runner did not run it.";
}

async function api(path, init) {
  const res = await fetch(`${API_BASE_URL}${path}`, init);
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.detail || `HTTP ${res.status}`);
  return body.data;
}

const post = (path, payload) =>
  api(path, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });

/* ------------------------------------------------------------------ */
/*  Artifacts column                                                   */
/* ------------------------------------------------------------------ */
const ICON_BUTTON =
  "inline-flex h-[1.875rem] w-[1.875rem] flex-none items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 shadow-sm transition";

/* A card's 30px icon button (the Log and Delete buttons' size). */
function SmallIconButton({ title, onClick, disabled = false, danger = false, children }) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      disabled={disabled}
      className={`${ICON_BUTTON} disabled:cursor-not-allowed disabled:opacity-50 ${
        danger ? "border-rose-200 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-600" : "hover:border-slate-300 hover:bg-slate-50 hover:text-slate-800"
      }`}
    >
      {children}
    </button>
  );
}

function ArtifactItem({ a, selected, onSelect, onCreate, onSteps, userkey, onDeleted }) {
  const latest = a.latest ? chipOf({ status: a.latest.status }) : null;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(a.test_key)}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && e.target === e.currentTarget && onSelect(a.test_key)}
      aria-current={selected ? "true" : undefined}
      className={[
        "group relative mx-3 my-2 flex cursor-pointer flex-col gap-2 rounded-xl border px-3.5 py-3 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
        selected
          ? "border-brand-300 bg-brand-50 shadow-sm ring-1 ring-brand-600/20"
          : "border-slate-200 bg-card hover:border-slate-300 hover:shadow-sm",
      ].join(" ")}
    >
      {/* The key with its latest result on the right, the title under it
          (two lines at most, the full title on hover), then a footer: its
          other chips (no counts of its executions), and the actions on the right. */}
      <span className="flex items-center gap-2">
        <Icons.ScrollText className={`h-4 w-4 flex-none ${selected ? "text-brand-600" : "text-slate-400"}`} />
        <span
          className={`min-w-0 flex-1 truncate text-[0.8125rem] font-semibold tracking-tight ${selected ? "text-brand-700" : "text-blue-700"}`}
          title={`Automates ${a.test_key}`}
        >
          {a.artifact?.artifact_key || a.test_key}
        </span>
        {a.running && <Chip chip={{ label: "Running", cls: TONES.running.pill, dot: TONES.running.dot }} />}
        {!a.running && latest && <Chip chip={latest} title="Latest execution" />}
      </span>
      <span
        className="block overflow-hidden text-[0.8125rem] leading-snug text-slate-700"
        style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}
        title={a.title}
      >
        {a.title}
      </span>
      <span className="flex items-center gap-1.5 border-t border-slate-100 pt-2">
        <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
          {a.drifted && <span className="rounded-full bg-amber-50 px-2 py-px text-[0.625rem] font-semibold text-amber-800">steps changed</span>}
          {a.status === "retired" && (
            <span className="rounded-full bg-slate-100 px-2 py-px text-[0.625rem] font-semibold text-slate-500" title="No longer generated for this App Page; kept for its Artifact">
              Retired
            </span>
          )}
        </span>
        <span className="flex flex-none items-center gap-1.5">
          {/* Icons only, in this order: create, edit, view, delete, log. */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onCreate(a);
            }}
            title="Create a new execution of this Artifact"
            aria-label={`Create an execution of ${a.test_key}`}
            className={`${ICON_BUTTON} hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700`}
          >
            <Icons.Plus className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onSteps(a, "edit");
            }}
            title="Edit the variable values"
            aria-label={`Edit the variables of ${a.test_key}`}
            className={`${ICON_BUTTON} hover:border-slate-300 hover:bg-slate-50 hover:text-slate-800`}
          >
            <Icons.Pencil className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onSteps(a, "view");
            }}
            title="View the steps and artifacts."
            aria-label={`See the script of ${a.test_key}`}
            className={`${ICON_BUTTON} hover:border-slate-300 hover:bg-slate-50 hover:text-slate-800`}
          >
            <Icons.Eye className="h-4 w-4" />
          </button>
          <AppPageDeleteButton
            what="artifact"
            testKey={a.test_key}
            artifactKey={a.artifact?.artifact_key}
            userkey={userkey}
            disabled={a.running}
            disabledReason="An execution of it is running. Stop it first."
            onDeleted={() => onDeleted(a)}
          />
          <AppPageActivityButton
            compact
            title={`${a.test_key} — ${a.title || "Test"}`}
            scope={{ test_key: a.test_key }}
            withExecutions
          />
        </span>
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Executions column                                                  */
/* ------------------------------------------------------------------ */
function ExecutionCard({ ex, scriptActive, busy, onOpen, onExecute, onWatch, onStop, userkey, onDeleted }) {
  const tone = toneOf(ex);
  const chip = chipOf(ex);
  const active = ex.status === "running";
  const runBy = ex.executed_by || ex.created_by || "";
  const pct = ex.live?.total ? Math.round((Math.max(0, ex.live.step - 1) / ex.live.total) * 100) : 0;
  /* The script line being run, as the Artifacts screen shows it. */
  const current = (ex.live?.current_step && stepToScript(ex.live.current_step)) || ex.live?.current || "";
  return (
    <article
      className={[
        "relative overflow-hidden rounded-xl border bg-card transition-all duration-200",
        active ? "border-blue-200 shadow-md shadow-blue-500/10 ring-1 ring-blue-500/20" : "border-slate-200/80 shadow-sm hover:border-slate-300 hover:shadow-md",
      ].join(" ")}
    >
      <span className={`absolute inset-y-0 left-0 w-1 ${tone.solid} ${active ? "animate-pulse" : ""}`} aria-hidden="true" />
      {/* As a Trial run's card: the key and name with the state on the
          right, what stopped it, then a footer with who ran it (initials) and the
          actions in the Artifact card's order (execute or watch and stop, view, delete, log). */}
      <div className="py-3.5 pl-5 pr-4">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <span className="block w-fit text-[0.8125rem] font-semibold tracking-tight text-blue-700">{ex.execution_key}</span>
            <button
              type="button"
              onClick={() => onOpen(ex)}
              title="Open this execution"
              className="mt-0.5 block max-w-full truncate text-left text-[0.8125rem] font-medium leading-snug text-slate-700 transition hover:text-brand-700"
            >
              {ex.name}
            </button>
          </div>
          {ex.status !== "ready" && (
            <span className={`inline-flex flex-none items-center gap-1.5 rounded-full px-2.5 py-1 text-[0.6875rem] font-semibold ring-1 ring-inset ${chip.cls}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${chip.dot}`} />
              {chip.label}
            </span>
          )}
        </div>
        {active && ex.live?.total > 0 && (
          <div className="mt-2">
            <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
              <div className="h-full rounded-full bg-blue-500 transition-all" style={{ width: `${pct}%` }} />
            </div>
            {current && (
              <p className="mt-1 truncate font-mono text-[0.625rem] text-slate-400" title={current}>
                {current}
              </p>
            )}
          </div>
        )}
        {!active && ex.message && ["blocked", "error", "interrupted", "cancelled"].includes(ex.status) && (
          <p className="mt-1.5 truncate text-[0.6875rem] text-rose-600" title={ex.message}>
            {ex.message}
          </p>
        )}
        {/* Skipped is not an error: it was never run, and the reason says what to change. */}
        {!active && ex.status === "skipped" && (
          <p className="mt-1.5 text-[0.6875rem] text-amber-800" title={ex.message || undefined}>
            Not run: {skipText(ex.message)}
          </p>
        )}
        {ex.ai_fix && (
          <div className="mt-2">
            <AiFixNotice fix={ex.ai_fix} compact />
          </div>
        )}
        <div className="mt-3 flex items-center gap-2 border-t border-slate-100 pt-3">
          <div className="min-w-0 flex-1">
            {runBy && (
              /* Initials only, as on a Trial run card; the name on hover. */
              <span
                className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-600 text-[0.625rem] font-semibold uppercase text-white"
                title={`Run by ${runBy}`}
                aria-label={`Run by ${runBy}`}
              >
                {initials(runBy)}
              </span>
            )}
          </div>
          {/* As the Artifact card: the action first (Execute, or watch and stop while it runs), then view,
              delete and log. */}
          {ex.status === "ready" && (
            /* The Trial run card's ▶: the same size and look as the card's other icons, in the run tone. */
            <IconButton
              title={scriptActive ? "This Artifact is already being executed in another execution" : "Execute the script now"}
              label="Execute"
              onClick={() => onExecute(ex)}
              disabled={scriptActive || Boolean(busy)}
              tone="run"
            >
              {busy ? <Icons.Spinner className="h-4 w-4 animate-spin" /> : <Icons.Play className="h-4 w-4" />}
            </IconButton>
          )}
          {active && (
            <>
              <SmallIconButton title="Watch the live browser" onClick={() => onWatch(ex)}>
                <Icons.Activity className="h-4 w-4" />
              </SmallIconButton>
              <SmallIconButton title="Stop this execution" onClick={() => onStop(ex)} disabled={busy || ex.live?.stopping} danger>
                {busy || ex.live?.stopping ? <Icons.Spinner className="h-4 w-4 animate-spin" /> : <Icons.X className="h-4 w-4" />}
              </SmallIconButton>
            </>
          )}
          {!active && ex.status !== "ready" && (
            <SmallIconButton title="View the results." onClick={() => onOpen(ex)}>
              <Icons.Eye className="h-4 w-4" />
            </SmallIconButton>
          )}
          <AppPageDeleteButton
            what="execution"
            executionKey={ex.execution_key}
            userkey={userkey}
            disabled={active || busy}
            disabledReason="It is running. Stop it first."
            onDeleted={() => onDeleted(ex)}
          />
          <AppPageActivityButton
            compact
            title={ex.test_key ? `${ex.test_key} — ${ex.name}` : `${ex.execution_key} — ${ex.name}`}
            scope={ex.test_key ? { test_key: ex.test_key } : { execution_key: ex.execution_key }}
            withExecutions
          />
        </div>
      </div>
    </article>
  );
}

/* ------------------------------------------------------------------ */
/*  Dialogs                                                            */
/* ------------------------------------------------------------------ */
function CreateDialog({ a, userkey, onClose, onCreate, onSaved }) {
  const [name, setName] = useState(() => executionName(a.test_key).slice(0, 300));
  const [description, setDescription] = useState("");
  const [autoLogin, setAutoLogin] = useState(true);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  /* The Test's variables: one the AI found no real value for (`a.missing`) is given one here, before Create. */
  const [variables, setVariables] = useState(a.variables || {});
  const stillMissing = (a.missing || []).filter((m) => !String(variables[m.name]?.value ?? "").trim());
  const blocked = stillMissing.length > 0;

  /* Create, or Create & Execute (the ▾ option): creates it, then runs it. */
  const create = async (execute) => {
    if (!name.trim() || busy || blocked) return;
    setMenuOpen(false);
    setBusy(execute ? "execute" : "create");
    setErr("");
    const msg = await onCreate(a, {
      name: name.trim(),
      description: description.trim(),
      auto_login: autoLogin,
      /* Always allowed: a test that submits or saves runs (the runner would otherwise skip it before step 1). */
      allow_saving: true,
      execute,
    });
    setBusy("");
    if (msg) setErr(msg);
  };
  const inputCls =
    "w-full rounded-lg border border-slate-300 bg-card px-3 py-2 text-sm text-slate-900 placeholder-slate-400 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/25";
  return (
    /* Full height only when it asks for variable values; otherwise as tall as its fields. */
    <Modal
      label="Create execution"
      onClose={menuOpen ? () => setMenuOpen(false) : onClose}
      busy={Boolean(busy)}
      md
      fit={!(a.missing || []).length}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          create(false);
        }}
        className="flex min-h-0 flex-1 flex-col"
      >
        <div className="flex flex-none items-center gap-3 bg-brand-700 px-6 py-4 text-white">
          <Icons.Plus className="h-5 w-5 flex-none" />
          <p className="text-base font-semibold">Create Execution</p>
        </div>
        <div className="cq-scroll min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-6">
          <p className="text-sm text-slate-600">
            <span className="font-semibold text-blue-700">{a.test_key}</span> · {a.title}
          </p>
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-slate-700">
              Execution name <span className="text-rose-500">*</span>
            </span>
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={300} autoFocus className={inputCls} />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-slate-700">Description</span>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className={inputCls} />
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={autoLogin}
              onChange={(e) => setAutoLogin(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-blue-600"
            />
            Specify Credentials
          </label>
          {(a.missing || []).length > 0 && (
            <div className="space-y-2">
              <p
                className={`rounded-lg border px-3 py-2 text-[0.75rem] ${
                  blocked ? "border-amber-200 bg-amber-50/70 text-amber-800" : "border-emerald-200 bg-emerald-50 text-emerald-800"
                }`}
              >
                {blocked
                  ? `No value was found for ${stillMissing
                      .map((m) => (m.hint ? `${m.hint} ({${m.name}})` : `{${m.name}}`))
                      .join(", ")}. Enter a valid value and press Save, then create the execution.`
                  : "Every value is set: the test can run."}
              </p>
              <AppPageVariables
                testKey={a.test_key}
                variables={variables}
                userkey={userkey}
                onSaved={(t, saved) => {
                  setVariables(t?.variables || {});
                  onSaved?.(`${a.test_key}: ${saved}`);
                }}
              />
            </div>
          )}
          {a.needs_data && !(a.missing || []).length && (
            <p className="rounded-lg border border-amber-200 bg-amber-50/70 px-3 py-2 text-[0.75rem] text-amber-800">
              A step still has a placeholder (&lt;…&gt;) where no value can be given, so this test is skipped. Create
              its tests again on App Pages.
            </p>
          )}
          {err && <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{err}</p>}
        </div>
        <div className="flex flex-none justify-end gap-2 border-t border-slate-200 bg-slate-50/70 px-6 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={Boolean(busy)}
            className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-600 shadow-sm hover:bg-slate-50"
          >
            Cancel
          </button>
          {/* Split button, as on the Artifacts screen: Create, and ▾ Create & Execute. */}
          <div className="relative flex">
            <button
              type="submit"
              disabled={!name.trim() || Boolean(busy) || blocked}
              title={blocked ? "Give the missing values first" : undefined}
              className="inline-flex items-center gap-1.5 rounded-l-lg bg-blue-600 px-4 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-60"
            >
              {busy ? <Icons.Spinner className="h-4 w-4 animate-spin" /> : <Icons.Plus className="h-4 w-4" />}
              {busy === "execute" ? "Creating & executing…" : busy ? "Creating…" : "Create"}
            </button>
            <button
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              disabled={!name.trim() || Boolean(busy) || blocked}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-label="More create options"
              className="inline-flex items-center rounded-r-lg border-l border-blue-500 bg-blue-600 px-2.5 py-1.5 text-white shadow-sm hover:bg-blue-700 disabled:opacity-60"
            >
              <Icons.ChevronDown className="h-4 w-4" />
            </button>
            {menuOpen && (
              <div role="menu" className="absolute bottom-full right-0 z-10 mb-1.5 min-w-[11rem] overflow-hidden rounded-lg bg-card shadow-lg ring-1 ring-slate-200">
                <button
                  type="button"
                  role="menuitem"
                  autoFocus
                  onClick={() => create(true)}
                  className="flex w-full items-center gap-2 px-3.5 py-2.5 text-left text-sm font-semibold text-slate-700 hover:bg-blue-50 hover:text-blue-700"
                >
                  <Icons.Play className="h-4 w-4" /> Create &amp; Execute
                </button>
              </div>
            )}
          </div>
        </div>
      </form>
    </Modal>
  );
}

/* An Artifact's dialog: `mode` "edit" changes its variable values, "view"
   shows its steps. */
function ScriptModal({ a, mode = "view", userkey, onClose, onSaved }) {
  const editing = mode === "edit";
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    let alive = true;
    api(`app-pages/tests/${encodeURIComponent(a.test_key)}`)
      .then((d) => alive && setData(d))
      .catch((e) => alive && setErr(e.message));
    return () => {
      alive = false;
    };
  }, [a.test_key]);
  return (
    <Modal label={editing ? "Artifact variables" : "Artifact steps"} onClose={onClose} wide>
      <div className="flex flex-none items-center gap-3 border-b border-slate-200 px-6 py-4">
        <Icons.ScrollText className="h-5 w-5 text-brand-600" />
        <p className="min-w-0 flex-1 truncate text-base font-semibold text-slate-800">
          {a.test_key} · {a.title}
        </p>
        <button type="button" onClick={onClose} aria-label="Close" className="rounded p-1 text-slate-400 hover:bg-slate-100">
          <Icons.X className="h-4 w-4" />
        </button>
      </div>
      <div className="cq-scroll min-h-0 flex-1 overflow-y-auto px-6 py-4">
        {err && <p className="text-sm text-red-600">{err}</p>}
        {!data && !err && (
          <Loader.Overlay variant="guide" label="Loading the script…" sublabel={`Fetching the Artifact of ${a.test_key}`} delay={0} />
        )}
        {editing && data && Object.keys(data.variables || {}).length > 0 && (
          <AppPageVariables
            testKey={a.test_key}
            variables={data.variables}
            userkey={userkey}
            onSaved={(t, saved) => {
              setData(t);
              onSaved?.(`${a.test_key}: ${saved}`);
            }}
          />
        )}
        {editing && data && !Object.keys(data.variables || {}).length && (
          <p className="text-sm text-slate-600">This Artifact has no variables to edit.</p>
        )}
        {!editing && data && <AppPageArtifactSteps lines={data.steps} steps={data.artifact?.steps || []} />}
      </div>
    </Modal>
  );
}

/* A screenshot URL from the API (e.g. /api/vision/runs/…/shots/…) made
   absolute against the API's own origin. */
const shotSrc = (u) => {
  if (!u) return "";
  if (u.startsWith("data:")) return u;
  return new URL(u, new URL(API_BASE_URL, window.location.origin)).toString();
};

/* An App Page execution in the shape ExecutionResultView reads: one test,
   every script step with its result and before/after screenshots. */
export function toResultData(d) {
  const script = (d.steps || []).filter((s) => s.action);
  const byIndex = Object.fromEntries((d.results || []).map((r) => [r.index, r]));
  const steps = script.map((step, i) => {
    const r = byIndex[i + 1];
    return {
      index: i + 1,
      step,
      name: step.description || step.action,
      status: r ? r.status : "not_run",
      error: r?.error || "",
      before: r?.before_url || "",
      after: r?.shot_url || "",
    };
  });
  const firstFailed = steps.find((s) => s.status === "failed");
  /* A blocked step (the App Page has no menu item): why, and what to do, in the amber "Blocked:" note. */
  const blockedNote = d.status === "blocked" ? steps.find((s) => s.status === "blocked")?.error || "" : "";
  /* Why nothing ran (sign-in, the App Page never opened…) — shown under the name. */
  const why = !blockedNote && ["blocked", "error", "interrupted"].includes(d.status) ? d.message : "";
  return {
    run_status: d.status,
    name: d.name,
    description: [d.description, why].filter(Boolean).join(" · "),
    created_by: d.executed_by || d.created_by || "",
    started_at: d.started_at,
    finished_at: d.finished_at,
    push_to_jira: false,
    live: d.live,
    ai_fix: d.ai_fix || null,
    /* The Artifact that ran (ART-RD-1 · its title): the result page's heading. */
    artifact: d.artifact || null,
    tests: [
      {
        xray_test_key: d.test_key,
        steps,
        passed_steps: steps.filter((s) => s.status === "passed").length,
        failed_step: firstFailed ? firstFailed.index : null,
        result_status: d.status === "blocked" ? "blocked" : undefined,
        blocked_note: blockedNote,
        started_at: d.started_at,
        finished_at: d.finished_at,
      },
    ],
  };
}

/* While it runs: the newest screenshot of the browser and the step it is on. */
function LivePanel({ data }) {
  const live = data.live || {};
  return (
    <section className="overflow-hidden rounded-2xl border border-blue-200 bg-card shadow-sm" aria-label="Live browser">
      <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-2.5 text-[0.75rem] text-slate-600">
        <span className="h-2 w-2 animate-pulse rounded-full bg-blue-500" />
        <span className="font-semibold text-slate-800">Live browser</span>
        <span className="min-w-0 flex-1 truncate">
          {live.current_index ? `Step ${live.current_index} of ${live.total}` : "Signing in and opening the App Page…"}
          {live.current ? ` — ${live.current}` : ""}
        </span>
      </div>
      <div className="bg-slate-900">
        {live.frame ? (
          <img src={shotSrc(live.frame)} alt="Live browser" className="mx-auto block h-auto max-h-[28rem] max-w-full" />
        ) : (
          <p className="py-16 text-center text-sm text-slate-400">The browser is starting…</p>
        )}
      </div>
    </section>
  );
}

/* The Artifacts screen's live-browser dialog, fed by an App Page execution. */
function AppPageLiveModal({ ex, onStop, onClose }) {
  const fetchLive = useCallback(async () => {
    const d = await api(`app-pages/executions/${encodeURIComponent(ex.execution_key)}`);
    const running = d.status === "running" || d.status === "ready";
    const live = d.live || {};
    /* A failed step: the live log says the script will be regenerated. */
    const aiLine = d.ai_fix?.status && aiFixText(d.ai_fix) ? [`✦ ${aiFixText(d.ai_fix)}`] : [];
    return {
      run_status: d.status,
      live: running
        ? {
            state: d.status === "ready" ? "queued" : "running",
            step: live.current_index || 0,
            total: live.total || (d.steps || []).length,
            frame: live.frame ? shotSrc(live.frame) : "",
            log: [...(live.log || []), ...aiLine],
            current: live.current_step ? stepToScript(live.current_step) : "",
          }
        : null,
    };
  }, [ex.execution_key]);
  return (
    <LiveRunModal
      ex={{ executionkey: ex.execution_key, name: ex.name }}
      fetchLive={fetchLive}
      startMaximized
      onStop={() => onStop(ex)}
      onClose={onClose}
    />
  );
}

function AppPageExecutionResult({ ex, onBack }) {
  const fetchResult = useCallback(
    async () => toResultData(await api(`app-pages/executions/${encodeURIComponent(ex.execution_key)}`)),
    [ex.execution_key]
  );
  return (
    <ExecutionResultView
      executionkey={ex.execution_key}
      fetchResult={fetchResult}
      shotSrc={shotSrc}
      backLabel="App Page Executions"
      renderLive={(data) => <LivePanel data={data} />}
      renderNotice={(data) => <AiFixNotice fix={data.ai_fix} />}
      onBack={onBack}
    />
  );
}

/* ------------------------------------------------------------------ */
/*  Screen                                                             */
/* ------------------------------------------------------------------ */
export default function AppPageExecutionsView({ userkey }) {
  const [pages, setPages] = useState([]);
  const [pagesLoading, setPagesLoading] = useState(true);
  const [page, setPage] = useState(null);
  const [artifacts, setArtifacts] = useState(null);
  const [selectedKey, setSelectedKey] = useState(null);
  const [executions, setExecutions] = useState(null);
  const [pageQuery, setPageQuery] = useState("");
  const [artifactQuery, setArtifactQuery] = useState("");
  const [execQuery, setExecQuery] = useState("");
  const [error, setError] = useState("");
  /* What was just done (created, executing, deleted, values saved), as a toast. */
  const [showToast, toast] = useToast();
  const [createFor, setCreateFor] = useState(null);
  const [scriptFor, setScriptFor] = useState(null);
  const [openExec, setOpenExec] = useState(null);
  const [watching, setWatching] = useState(null);
  const [busyKey, setBusyKey] = useState(null);

  const loadPages = useCallback(async () => {
    try {
      setPages((await api("app-pages")) || []);
    } catch (e) {
      setError(`Could not load App Pages: ${e.message}`);
    } finally {
      setPagesLoading(false);
    }
  }, []);
  const loadArtifacts = useCallback(async (p) => {
    if (!p) return;
    try {
      const data = await api(`app-pages/${encodeURIComponent(p)}/artifacts`);
      setArtifacts(data?.artifacts || []);
    } catch (e) {
      setError(`Could not load artifacts: ${e.message}`);
    }
  }, []);
  const loadExecutions = useCallback(async (key) => {
    if (!key) return;
    try {
      setExecutions((await api(`app-pages/tests/${encodeURIComponent(key)}/executions`)) || []);
    } catch (e) {
      setError(`Could not load executions: ${e.message}`);
    }
  }, []);

  useEffect(() => {
    loadPages();
  }, [loadPages]);

  /* Keep running executions moving, and the AI's repair after one fails. */
  const anyRunning =
    (executions || []).some((e) => e.status === "running" || aiFixActive(e.ai_fix)) || (artifacts || []).some((a) => a.running);
  useEffect(() => {
    if (!anyRunning) return undefined;
    const t = setInterval(() => {
      loadArtifacts(page);
      loadExecutions(selectedKey);
    }, 3000);
    return () => clearInterval(t);
  }, [anyRunning, page, selectedKey, loadArtifacts, loadExecutions]);

  const pickPage = (p) => {
    if (p === page) return;
    setPage(p);
    setArtifacts(null);
    setSelectedKey(null);
    setExecutions(null);
    setArtifactQuery("");
    setExecQuery("");
    loadArtifacts(p);
  };
  const pickArtifact = (key) => {
    if (key === selectedKey) return;
    setSelectedKey(key);
    setExecutions(null);
    setExecQuery("");
    loadExecutions(key);
  };
  const refresh = () => {
    setError("");
    loadPages();
    loadArtifacts(page);
    loadExecutions(selectedKey);
  };

  const createExecution = async (a, { execute, ...payload }) => {
    try {
      const ex = await post(`app-pages/tests/${encodeURIComponent(a.test_key)}/executions`, {
        ...payload,
        execute,
        userkey: userkey || null,
      });
      setCreateFor(null);
      showToast(
        execute
          ? `${ex.execution_key} has been created and is executing.`
          : `${ex.execution_key} has been created successfully. Press Execute on its card to run it.`,
      );
      if (execute) setWatching(ex);
      setSelectedKey(a.test_key);
      loadExecutions(a.test_key);
      loadArtifacts(page);
      return "";
    } catch (e) {
      return e.message;
    }
  };
  const executeOne = async (ex) => {
    setBusyKey(ex.execution_key);
    try {
      await post(`app-pages/executions/${encodeURIComponent(ex.execution_key)}/execute`, { userkey: userkey || null });
      showToast(`${ex.execution_key} is executing.`);
      setWatching(ex);
      loadExecutions(selectedKey);
      loadArtifacts(page);
    } catch (e) {
      setError(`Could not execute: ${e.message}`);
    } finally {
      setBusyKey(null);
    }
  };

  const stopExecution = async (ex) => {
    setBusyKey(ex.execution_key);
    try {
      await post(`app-pages/executions/${encodeURIComponent(ex.execution_key)}/cancel`, {});
    } catch (e) {
      setError(`Could not stop: ${e.message}`);
    } finally {
      setBusyKey(null);
      loadExecutions(selectedKey);
      loadArtifacts(page);
    }
  };

  /* A deleted Artifact leaves the list (its executions stay, as records). */
  const artifactDeleted = (a) => {
    showToast(`${a.artifact?.artifact_key || a.test_key} has been deleted successfully.`);
    if (a.test_key === selectedKey) {
      setSelectedKey(null);
      setExecutions(null);
    }
    loadArtifacts(page);
    loadPages();
  };
  const executionDeleted = (ex) => {
    showToast(`${ex.execution_key} has been deleted successfully.`);
    loadExecutions(selectedKey);
    loadArtifacts(page);
  };

  const selected = (artifacts || []).find((a) => a.test_key === selectedKey) || null;
  const runningCount = (artifacts || []).filter((a) => a.running).length;
  const scriptActive = (executions || []).some((e) => e.status === "running");
  const shownPages = pages.filter((p) => matchesQuery(pageQuery, p.app_page_id));
  const shownArtifacts = (artifacts || []).filter((a) =>
    matchesQuery(artifactQuery, a.artifact?.artifact_key, a.test_key, a.title)
  );
  const shownExecutions = (executions || []).filter((ex) =>
    matchesQuery(execQuery, ex.execution_key, ex.name, ex.status, ex.executed_by, ex.created_by)
  );

  /* An execution opens on its own page, like on the Artifacts screen. */
  if (openExec) {
    return (
      <AppPageExecutionResult
        ex={openExec}
        onBack={() => {
          setOpenExec(null);
          loadExecutions(selectedKey);
          loadArtifacts(page);
        }}
      />
    );
  }

  let artifactsBody;
  if (!page) {
    artifactsBody = (
      <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
        <Icons.ScrollText className="h-6 w-6 text-slate-300" />
        <p className="text-xs text-slate-400">Select an App Page to see its artifacts.</p>
      </div>
    );
  } else if (artifacts === null) {
    artifactsBody = (
      <Loader.Overlay variant="guide" label="Loading artifacts…" sublabel={`Fetching the artifacts of ${page}`} delay={0} />
    );
  } else if (artifacts.length === 0) {
    artifactsBody = (
      <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
        <Icons.ScrollText className="h-6 w-6 text-slate-300" />
        <p className="text-xs font-medium text-slate-600">No artifacts for this App Page yet.</p>
        <p className="text-[0.6875rem] text-slate-400">Create them on App Page with Create Artifact.</p>
      </div>
    );
  } else if (shownArtifacts.length === 0) {
    artifactsBody = <NoMatches query={artifactQuery} />;
  } else {
    artifactsBody = shownArtifacts.map((a) => (
      <ArtifactItem
        key={a.test_key}
        a={a}
        selected={a.test_key === selectedKey}
        onSelect={pickArtifact}
        onCreate={setCreateFor}
        onSteps={(item, mode) => setScriptFor({ a: item, mode })}
        userkey={userkey}
        onDeleted={artifactDeleted}
      />
    ));
  }

  let executionsBody;
  if (!selected) {
    executionsBody = (
      <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
        <Icons.JiraTestExecution />
        <p className="text-xs text-slate-400">Select an artifact to see its trial runs.</p>
      </div>
    );
  } else if (executions === null) {
    executionsBody = (
      <Loader.Overlay
        variant="guide"
        label="Loading executions…"
        sublabel={`Fetching the executions of ${selected.test_key}`}
        delay={0}
      />
    );
  } else if (executions.length === 0) {
    executionsBody = (
      <div className="flex flex-col items-center gap-1.5 px-6 py-12 text-center">
        <Icons.JiraTestExecution />
        <p className="text-sm font-semibold text-slate-700">{selected.artifact?.artifact_key || selected.test_key} has not been executed yet</p>
        <p className="max-w-sm text-xs text-slate-500">Its executions will be listed here. To add one, use Create (+) on the Artifact.</p>
      </div>
    );
  } else if (shownExecutions.length === 0) {
    executionsBody = <NoMatches query={execQuery} />;
  } else {
    executionsBody = (
      <div className="space-y-3 p-4">
        {shownExecutions.map((ex) => (
          <ExecutionCard
            key={ex.execution_key}
            ex={ex}
            scriptActive={scriptActive}
            busy={busyKey === ex.execution_key}
            onOpen={setOpenExec}
            onExecute={executeOne}
            onWatch={setWatching}
            onStop={stopExecution}
            userkey={userkey}
            onDeleted={executionDeleted}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-8rem)] min-h-0 flex-col gap-4">
      <div className="flex flex-none items-center gap-3">
        <h1 className="min-w-0 flex-1 text-2xl font-bold tracking-tight text-slate-900">App Page Executions</h1>
        {runningCount > 0 && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-1 text-[0.6875rem] font-semibold text-blue-700 ring-1 ring-blue-600/20">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-blue-500" />
            {runningCount} running
          </span>
        )}
        <button
          type="button"
          onClick={refresh}
          title="Reload App Pages, artifacts and executions"
          className="flex h-[2.375rem] items-center gap-1.5 rounded-lg border border-slate-200 bg-card px-3 text-sm text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-slate-900"
        >
          <Icons.Refresh className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {error && <div className="flex-none rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
      {toast}

      <div className="flex min-h-0 flex-1 overflow-hidden rounded-2xl border border-slate-200 bg-card shadow-sm">
        {/* Level 1 — App Pages */}
        <aside className="flex w-60 flex-none flex-col overflow-hidden border-r border-slate-200 bg-slate-50/40 xl:w-72 3xl:w-80 4xl:w-[28rem]" aria-label="App Pages">
          <div className="flex flex-none items-center justify-between border-b border-slate-200 bg-slate-100/70 px-4 h-14">
            <span className="flex items-center gap-2 text-sm font-semibold text-slate-800">
              <Icons.Grid className="h-5 w-5 text-blue-600" /> App Pages
            </span>
            <span className="rounded-full bg-white px-2 py-0.5 text-[0.6875rem] font-semibold text-slate-500 ring-1 ring-slate-200">{shownPages.length}</span>
          </div>
          <div className="flex-none border-b border-slate-200 px-3 py-2">
            <ListSearch value={pageQuery} onChange={setPageQuery} placeholder="Search App Pages…" label="Search App Pages" />
          </div>
          <div className="cq-scroll min-h-0 flex-1 divide-y divide-slate-100 overflow-y-auto">
            {pagesLoading ? (
              <Loader.Overlay variant="guide" label="Loading App Pages…" sublabel="Fetching the App Pages and their artifacts" delay={0} />
            ) : shownPages.length === 0 && pageQuery.trim() ? (
              <NoMatches query={pageQuery} />
            ) : (
              shownPages.map((p) => (
                <button
                  key={p.app_page_id}
                  type="button"
                  onClick={() => pickPage(p.app_page_id)}
                  className={[
                    "flex w-full flex-col border-l-[0.1875rem] px-4 py-3 text-left transition-colors",
                    page === p.app_page_id ? "border-brand-600 bg-brand-50" : "border-transparent hover:bg-slate-50",
                  ].join(" ")}
                >
                  <span className={`text-[0.8125rem] font-semibold ${page === p.app_page_id ? "text-brand-700" : "text-blue-700"}`}>
                    {p.app_page_id}
                  </span>
                  <span className="mt-0.5 text-[0.75rem] text-slate-500">{plural(p.artifacts ?? p.automated ?? 0, "artifact")}</span>
                </button>
              ))
            )}
          </div>
        </aside>

        {/* Level 2 — Artifacts of the App Page */}
        <section className="flex w-[19rem] flex-none flex-col overflow-hidden border-r border-slate-200 bg-slate-50/40 xl:w-[23rem] 2xl:w-[26rem] 4xl:w-[36rem]" aria-label="Artifacts">
          <div className="flex flex-none items-center justify-between border-b border-slate-200 bg-slate-100/70 px-4 h-14">
            <span className="flex items-center gap-2 text-sm font-semibold text-slate-800">
              <Icons.ScrollText className="h-5 w-5 text-brand-600" /> Artifacts
            </span>
            <span className="flex items-center gap-2">
              <span className="rounded-full bg-white px-2 py-0.5 text-[0.6875rem] font-semibold text-slate-500 ring-1 ring-slate-200">
                {shownArtifacts.length}
              </span>
              {/* Everything logged on this App Page: its Tests, Artifacts and executions, deletes included. */}
              {page && <AppPageActivityButton compact title={`${page} — Artifacts`} scope={{ app_page_id: page }} withExecutions openOn="" />}
            </span>
          </div>
          {page && (
            <div className="flex-none border-b border-slate-200 px-3 py-2">
              <ListSearch
                value={artifactQuery}
                onChange={setArtifactQuery}
                placeholder="Search artifacts by key or title…"
                label="Search artifacts"
              />
            </div>
          )}
          <div className="cq-scroll min-h-0 flex-1 overflow-y-auto py-1">{artifactsBody}</div>
        </section>

        {/* Level 3 — Executions of the Artifact */}
        <section className="flex min-w-0 flex-1 flex-col overflow-hidden" aria-label="Executions">
          <div className="flex flex-none items-center justify-between border-b border-slate-200 bg-slate-100/70 px-4 h-14">
            <span className="flex items-center gap-2 text-sm font-semibold text-slate-800">
              <Icons.JiraTestExecution /> Executions
            </span>
            {selected && (
              <span className="rounded-full bg-white px-2 py-0.5 text-[0.6875rem] font-semibold text-slate-500 ring-1 ring-slate-200">
                {shownExecutions.length}
              </span>
            )}
          </div>
          {selected && (
            <div className="flex-none border-b border-slate-200 px-3 py-2">
              <ListSearch
                value={execQuery}
                onChange={setExecQuery}
                placeholder="Search executions by key, name, status or user…"
                label="Search executions"
              />
            </div>
          )}
          <div className="cq-scroll min-h-0 flex-1 overflow-y-auto">{executionsBody}</div>
        </section>
      </div>

      {createFor && (
        <CreateDialog
          a={createFor}
          userkey={userkey}
          onClose={() => {
            setCreateFor(null);
            /* A value given in the dialog changes the Artifact's flags. */
            loadArtifacts(page);
          }}
          onCreate={createExecution}
          onSaved={showToast}
        />
      )}
      {scriptFor && <ScriptModal a={scriptFor.a} mode={scriptFor.mode} userkey={userkey} onClose={() => setScriptFor(null)} onSaved={showToast} />}
      {watching && (
        <AppPageLiveModal
          ex={watching}
          onStop={stopExecution}
          onClose={() => {
            setWatching(null);
            loadExecutions(selectedKey);
            loadArtifacts(page);
          }}
        />
      )}
    </div>
  );
}
